import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import BottomNav from "@/components/BottomNav";
import PhoneFrame from "@/components/PhoneFrame";
import NotificationPoller from "@/components/NotificationPoller";
import OfflineBanner from "@/components/OfflineBanner";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Chat from "@/pages/Chat";
import Vault from "@/pages/Vault";
import Notes from "@/pages/Notes";
import Documents from "@/pages/Documents";
import Passwords from "@/pages/Passwords";
import Photos from "@/pages/Photos";
import Reminders from "@/pages/Reminders";
import Scanner from "@/pages/Scanner";
import SearchPage from "@/pages/Search";
import Profile from "@/pages/Profile";
import Legacy from "@/pages/Legacy";
import LegacyInvite from "@/pages/LegacyInvite";
import Household from "@/pages/Household";
import JoinHousehold from "@/pages/JoinHousehold";
import Plan from "@/pages/Plan";
import GoogleCallback from "@/pages/GoogleCallback";
import Journal from "@/pages/Journal";
import Videos from "@/pages/Videos";
import Media from "@/pages/Media";
import About from "@/pages/About";
import Library from "@/pages/Library";
import Capture from "@/pages/Capture";
import ShareTarget from "@/pages/ShareTarget";
import Investments from "@/pages/Investments";
import Insurance from "@/pages/Insurance";
import Medical from "@/pages/Medical";
import Travel from "@/pages/Travel";
import CalendarPage from "@/pages/Calendar";
import VoiceNotes from "@/pages/VoiceNotes";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DeleteAccount from "@/pages/DeleteAccount";

function Protected({ children }) {
  const { user, token, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!token || !user) return <Navigate to="/auth?mode=login" replace state={{ from: loc.pathname }} />;
  return children;
}

function AppLayout() {
  const location = useLocation();
  return (
    <PhoneFrame>
      <div className="flex flex-col h-full min-h-[100dvh]">
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {/* Keyed by pathname → re-triggers page-in animation on every route change */}
          <div key={location.pathname} className="impo-page">
            <Outlet />
          </div>
        </div>
        <OfflineBanner />
        <BottomNav />
        <NotificationPoller />
      </div>
    </PhoneFrame>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/data-deletion" element={<DeleteAccount />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/legacy/accept/:token" element={<LegacyInvite />} />
          <Route path="/household/join/:token" element={<JoinHousehold />} />
          <Route
            path="/app"
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route index element={<Navigate to="chat" replace />} />
            <Route path="home" element={<Navigate to="/app/chat" replace />} />
            <Route path="chat" element={<Chat />} />
            <Route path="share" element={<ShareTarget />} />
            <Route path="vault" element={<Vault />} />
            <Route path="notes" element={<Notes />} />
            <Route path="documents" element={<Documents />} />
            <Route path="passwords" element={<Passwords />} />
            <Route path="photos" element={<Navigate to="/app/media?tab=photos" replace />} />
            <Route path="videos" element={<Navigate to="/app/media?tab=videos" replace />} />
            <Route path="media" element={<Media />} />
            <Route path="reminders" element={<Reminders />} />
            <Route path="scanner" element={<Scanner />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="legacy" element={<Legacy />} />
            <Route path="household" element={<Household />} />
            <Route path="plan" element={<Plan />} />
            <Route path="journal" element={<Journal />} />
            <Route path="about" element={<About />} />
            <Route path="library" element={<Library />} />
            <Route path="capture" element={<Capture />} />
            <Route path="investments" element={<Investments />} />
            <Route path="insurance" element={<Insurance />} />
            <Route path="medical" element={<Medical />} />
            <Route path="travel" element={<Travel />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="voice-notes" element={<VoiceNotes />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
